import axios from 'axios'
import toast from 'react-hot-toast'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000,
})

// Injecte le token JWT dans chaque requête
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user_email')
      window.location.href = '/login'
      return Promise.reject(err)
    }
    // FastAPI peut renvoyer detail = string OU array de validation errors
    // (objets {type, loc, msg, input, url}). On sérialise proprement pour
    // ne jamais passer un objet à toast/JSX (cause React error #31).
    let detail = err.response?.data?.detail
    let msg
    if (typeof detail === 'string') {
      msg = detail
    } else if (Array.isArray(detail) && detail.length > 0) {
      msg = detail.map(e => {
        if (typeof e === 'string') return e
        const loc = Array.isArray(e?.loc) ? e.loc.join('.') : ''
        return `${loc ? loc + ' : ' : ''}${e?.msg || JSON.stringify(e)}`
      }).join(' · ')
    } else if (detail) {
      msg = JSON.stringify(detail)
    } else {
      msg = err.message || 'Erreur réseau'
    }
    if (!err.config?.suppressToast) {
      toast.error(msg)
    }
    // Surcharge la detail pour les caller code qui font e.response.data.detail
    if (err.response?.data && typeof detail !== 'string') {
      err.response.data.detail = msg
    }
    return Promise.reject(err)
  }
)

// Auth
export const login = (data) => api.post('/auth/login', data)
export const register = (data) => api.post('/auth/register', data)
export const googleLogin = (credential) => api.post('/auth/google', { credential })
export const getAuthConfig = () => api.get('/auth/config')
export const checkHasAccount = () => api.get('/auth/me')

// Téléchargements (app mobile)
export const getAndroidApkInfo = () => api.get('/downloads/android/info', { suppressToast: true })
export const ANDROID_APK_URL = (import.meta.env.VITE_API_BASE_URL || '/api') + '/downloads/android'

// Compte rendu mensuel
export const getMonthlyReport = (year, month) => api.get('/reports/monthly', { params: { year, month } })
export const geocodeLocation = (q) => api.get('/reports/geocode', { params: { q }, suppressToast: true })

// Telegram bot
export const getTelegramStatus = () => api.get('/telegram/status', { suppressToast: true })
export const setTelegramWebhook = () => api.post('/telegram/set-webhook')
export const generateTelegramLinkCode = () => api.post('/telegram/generate-link-code')
export const unlinkTelegram = () => api.post('/telegram/unlink')

// Notes
export const listNotes = (params = {}) => api.get('/notes', { params })
export const createNote = (data) => api.post('/notes', data)
export const updateNote = (id, data) => api.put(`/notes/${id}`, data)
export const deleteNote = (id) => api.delete(`/notes/${id}`)
export const semanticReindex = () => api.post('/notes/semantic/reindex', null, { timeout: 120000 })
export const semanticSearch = (q) => api.get('/notes/semantic/search', { params: { q } })
export const listUsers = () => api.get('/auth/users')
export const getUsersActivity = () => api.get('/auth/users/activity')
export const getUserCompanies = (id) => api.get(`/auth/users/${id}/companies`)
export const getUserActivityLog = (id) => api.get(`/auth/users/${id}/activity`)
export const adminCreateUser = (data) => api.post('/auth/users', data)
export const setUserAdmin = (id, isAdmin) => api.put(`/auth/users/${id}/admin`, { is_admin: isAdmin })
export const getUserDeletionPreview = (id) => api.get(`/auth/users/${id}/deletion-preview`)
export const deleteUser = (id) => api.delete(`/auth/users/${id}`)

// Settings
export const getSettings = () => api.get('/settings')
export const updateSettings = (data) => api.put('/settings', data)
export const getCadences = () => api.get('/settings/cadences')
export const updateCadences = (cadences) => api.put('/settings/cadences', { cadences })
export const getNafCodes = () => api.get('/settings/naf-codes')
export const addNafCode = (data) => api.post('/settings/naf-codes', data)
export const deleteNafCode = (code) => api.delete(`/settings/naf-codes/${code}`)

// Companies
export const searchCompanies = (params) => api.post('/companies/search', params)
export const saveCompany = (data) => api.post('/companies/save', data)
export const saveBatch = (companies) => api.post('/companies/save-batch', companies)
export const listCompanies = (params) => api.get('/companies', { params })
export const getCompany = (id) => api.get(`/companies/${id}`)
export const getCompanyBySiren = (siren) => api.get(`/companies/siren/${siren}`)
export const updateCompanyStatus = (id, data) => api.put(`/companies/${id}/status`, data)
export const updateCompanyNotes = (id, notes) => api.put(`/companies/${id}/notes`, null, { params: { notes } })
export const addContact = (companyId, data) => api.post(`/companies/${companyId}/contacts`, { ...data, company_id: companyId })
export const updateContact = (companyId, contactId, data) => api.put(`/companies/${companyId}/contacts/${contactId}`, data)
export const deleteContact = (companyId, contactId) => api.delete(`/companies/${companyId}/contacts/${contactId}`)
export const addAction = (companyId, data) => api.post(`/companies/${companyId}/actions`, { ...data, company_id: companyId })
export const deleteCompany = (id) => api.delete(`/companies/${id}`)
export const enrichCompany = (id) => api.get(`/companies/${id}/enrich`)
export const refreshCompanyFromGouv = (id) => api.post(`/companies/${id}/refresh-gouv`, null, { timeout: 30000 })
export const enrichOfficial = (id) => api.post(`/companies/${id}/enrich-official`, null, { timeout: 40000 })
export const getBodaccEvents = (id, limit = 20) => api.get(`/companies/${id}/bodacc-events`, { params: { limit }, timeout: 20000 })
export const reclassifySegments = () => api.post('/companies/reclassify-segments', null, { timeout: 60000 })
export const evaluateRiskBatch = (params = {}) => api.post('/companies/evaluate-risk-batch', params, { timeout: 180000 })
// 5 min — la 1re recherche géocode tous les prospects (1 req/sec Nominatim).
// Après ça, les coords sont en cache DB → instantané.
export const findNearbyProspects = (params = {}) => api.get('/companies/tour/nearby', { params, timeout: 300000 })
export const tourDiscover = (city, radius_km = 50) => api.post('/companies/tour/discover', { city, radius_km }, { timeout: 90000 })
export const tourDiscoverDept = (dept) => api.post('/companies/tour/discover', { dept }, { timeout: 90000 })
export const tourDiscoverAdd = (item) => api.post('/companies/tour/discover-add', item, { timeout: 30000 })
export const getAllProspectCoords = (excludeWonLost = false) => api.get('/companies/tour/all-coords', { params: { exclude_won_lost: excludeWonLost }, timeout: 15000 })
export const geocodeAllProspects = (maxCalls = 50) => api.post('/companies/tour/geocode-all', null, { params: { max_calls: maxCalls }, timeout: 180000 })
// Tournée par département : départements dispo, itinéraire optimisé, PDF, validation visite
export const getTourDepartments = () => api.get('/companies/tour/departments', { timeout: 15000 })
export const getTourByDepartment = (departement, opts = {}) => api.get('/companies/tour/by-department', { params: { departement, ...opts }, timeout: 180000 })
export const downloadTourPdf = (departement, opts = {}) => api.get('/companies/tour/pdf', { params: { departement, ...opts }, responseType: 'blob', timeout: 180000 })
export const markTourVisit = (companyId, body) => api.post(`/companies/${companyId}/tour-visit`, body)
export const tourToAgenda = (body) => api.post('/companies/tour/agenda', body, { timeout: 180000 })
export const listTours = () => api.get('/companies/tours', { timeout: 15000 })
export const deleteTour = (tourId, withEvents = false) => api.delete(`/companies/tours/${tourId}`, { params: { with_events: withEvents } })
// Supprime tout le bloc agenda d'une tournée d'un jour (events + Google + mémoire)
export const deleteTourAgenda = (date, departement = '') => api.delete('/companies/tour/agenda', { params: { date, departement } })
// Suggestions d'entreprises à visiter à proximité des RDV d'un jour donné
export const tourAroundRdv = (date, radius_km = 20) => api.get('/companies/tour/around-rdv', { params: { date, radius_km }, timeout: 60000 })
export const getTourDayBusy = (date) => api.get('/companies/tour/day-busy', { params: { date }, timeout: 15000 })
export const tourIgnoreEvent = (event_id, ignored = true) => api.post('/companies/tour/ignore-event', { event_id, ignored })
// Assistant « Conseille-moi » : tournée conseillée autour des RDV, bornée par la contrainte de retour déduite de l'agenda
export const tourAdvise = (body) => api.post('/companies/tour/advise', body, { timeout: 120000 })
// Contacts LinkedIn (CRM personnes, séparé du CRM entreprises) — alimenté par import CSV TotLeads
export const importLeads = (filename, content) => api.post('/leads/import', { filename, content }, { timeout: 120000 })
export const listLeads = (params = {}) => api.get('/leads', { params })
export const leadFacets = () => api.get('/leads/facets', { timeout: 15000 })
export const leadStats = () => api.get('/leads/stats', { timeout: 15000 })
export const updateLead = (id, data) => api.put(`/leads/${id}`, data)
export const deleteLead = (id) => api.delete(`/leads/${id}`)
export const batchDeleteLeads = (body) => api.post('/leads/batch-delete', body)
export const adminCleanupByNaf = (naf_codes, dry_run = true, confirm = '') => api.post('/companies/admin/cleanup-by-naf', { naf_codes, dry_run, confirm }, { timeout: 120000 })
export const adminCleanupByCategory = (categories, dry_run = true, confirm = '') => api.post('/companies/admin/cleanup-by-category', { categories, dry_run, confirm }, { timeout: 120000 })
export const getSupplierSuggestions = (id, top = 3) => api.get(`/companies/${id}/supplier-suggestions`, { params: { top }, timeout: 15000 })
export const generateSupplierPitch = (id, supplierId, channel = 'email') => api.post(`/companies/${id}/supplier-pitch`, { supplier_id: supplierId, channel }, { timeout: 60000 })
export const classifyCompany = (id, force = false) => api.post(`/companies/${id}/classify`, force ? { force: true } : null, { timeout: 90000 })
export const setCompanyCategory = (id, category) => api.put(`/companies/${id}/category`, { category })
export const scoreCompany = (id) => api.post(`/companies/${id}/score`, null, { timeout: 90000 })
export const generateIcebreaker = (id, smart = false) => api.post(`/companies/${id}/icebreaker`, null, { params: { smart }, timeout: 90000 })
export const suggestNextAction = (id) => api.post(`/companies/${id}/next-action`, null, { timeout: 90000 })
export const getCompanySignals = (id, sinceDays = 365) => api.get(`/companies/${id}/signals`, { params: { since_days: sinceDays } })
export const findContacts = (id) => api.post(`/companies/${id}/find-contacts`, null, { params: { auto_save: true }, timeout: 90000 })
export const getEntryStrategy = (id) => api.post(`/companies/${id}/entry-strategy`, null, { timeout: 90000 })
export const findDeciders = (id) => api.post(`/companies/${id}/find-deciders`, null, { timeout: 90000 })
export const linkedinAssist = (id, payload) => api.post(`/companies/${id}/linkedin-assist`, payload, { timeout: 90000 })
export const icebreakerVariants = (id, smart = false) => api.post(`/companies/${id}/icebreaker-variants`, null, { params: { smart }, timeout: 90000 })
export const icebreakerChoice = (id, angle) => api.post(`/companies/${id}/icebreaker-choice`, { angle })
export const createBucket = (bucket = '') => api.post('/backup/remote/create-bucket', { bucket }, { timeout: 30000 })
export const discoverMaps = (query) => api.post('/companies/discover-maps', { query }, { timeout: 30000 })
export const findLookalikes = (seedIds) => api.post('/companies/lookalike', { seed_ids: seedIds }, { timeout: 60000 })
export const importFromUrl = (url) => api.post('/companies/import-url', { url }, { timeout: 45000 })

// Alerts + notifications (Phase 3)
export const listAlerts = () => api.get('/alerts')
export const createAlert = (data) => api.post('/alerts', data)
export const deleteAlert = (id) => api.delete(`/alerts/${id}`)
export const toggleAlert = (id) => api.put(`/alerts/${id}/toggle`)
export const runAlertsNow = () => api.post('/alerts/run-now', null, { timeout: 120000 })
export const listNotifications = (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } })
export const getUnreadCount = () => api.get('/notifications/unread-count')
export const markNotifRead = (id) => api.put(`/notifications/${id}/read`)
export const markAllNotifRead = () => api.put('/notifications/read-all')
export const getRadar = () => api.get('/dashboard/radar')

// Feedback bêta
export const submitFeedback = (data) => api.post('/feedback', data)
export const listFeedback = () => api.get('/feedback')
export const setFeedbackStatus = (id, status) => api.put(`/feedback/${id}/status`, { status })
export const getSystemHealth = () => api.get('/dashboard/system-health')
export const getIngestionHealth = () => api.get('/dashboard/ingestion-health')
export const getLlmCosts = (days = 30) => api.get('/dashboard/llm-costs', { params: { days } })
export const sendLeadFeedback = (id, value) => api.post(`/companies/${id}/lead-feedback`, { value })

// Templates ICP
export const listICPTemplates = () => api.get('/icp-templates')
export const createICPTemplate = (data) => api.post('/icp-templates', data)
export const applyICPTemplate = (id) => api.post(`/icp-templates/${id}/apply`)
export const deleteICPTemplate = (id) => api.delete(`/icp-templates/${id}`)

// Phase 4
export const batchStatus = (ids, status) => api.post('/companies/batch-status', { ids, status })
export const batchDelete = (ids) => api.post('/companies/batch-delete', { ids })
export const batchSource = (ids, source) => api.post('/companies/batch-source', { ids, source })

// Agenda
export const listEvents = (params = {}) => api.get('/agenda', { params })
export const createEvent = (data) => api.post('/agenda', data)
export const updateEvent = (id, data) => api.put(`/agenda/${id}`, data)
export const deleteEvent = (id) => api.delete(`/agenda/${id}`)
export const getAgendaFeedUrl = () => api.get('/agenda/feed-url')
export const resetAgendaFeedUrl = () => api.post('/agenda/feed-url/reset')
export const googleStatus = () => api.get('/agenda/google/status')
export const googleCalendars = () => api.get('/agenda/google/calendars')
export const googleConnect = () => api.get('/agenda/google/connect')
export const googleSync = () => api.post('/agenda/google/sync', null, { timeout: 90000 })
export const googleDisconnect = () => api.post('/agenda/google/disconnect')
export const getStaleProspects = (days = 7) => api.get('/companies/stale', { params: { days } })
export const conversationAssist = (id, conversation) => api.post(`/companies/${id}/conversation-assist`, { conversation })
export const updateCompanyFields = (id, fields) => api.put(`/companies/${id}/fields`, fields)
export const analyzeSite = (id) => api.post(`/companies/${id}/analyze-site`, null, { timeout: 90000 })

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats')
export const getDashboardToday = () => api.get('/dashboard/today')
export const getSalesObjectives = () => api.get('/dashboard/objectives')
export const discoverSegmentTargets = (segment, n = 8) => api.post('/dashboard/discover-segment-targets', { segment, n }, { timeout: 60000 })
export const getNewCompanies = (months = 6, sort = 'recent') => api.get('/dashboard/new-companies', { params: { months, sort } })

// Export
export const exportCsv = (params) => api.get('/export/csv', { params, responseType: 'blob' })
export const exportExcel = (params) => api.get('/export/excel', { params, responseType: 'blob' })

// LinkedIn posts
export const listPosts = () => api.get('/posts')
export const createPost = (data) => api.post('/posts', data)
export const updatePost = (id, data) => api.put(`/posts/${id}`, data)
export const deletePost = (id) => api.delete(`/posts/${id}`)
export const updatePostStatus = (id, status) => api.put(`/posts/${id}/status`, null, { params: { status } })
export const generatePost = (data) => api.post('/posts/generate', data)
export const getTemplates = () => api.get('/posts/templates/list')
export const getSuggestedHashtags = () => api.get('/posts/hashtags/suggest')

// Fournisseurs (veille)
export const listSuppliers = () => api.get('/suppliers')
export const createSupplier = (data) => api.post('/suppliers', data)
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data)
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`)
export const markSupplierChecked = (id) => api.post(`/suppliers/${id}/checked`)
export const refreshSupplierFeed = (id) => api.post(`/suppliers/${id}/refresh`, null, { timeout: 60000 })
export const refreshAllFeeds = () => api.post('/suppliers/refresh-all', null, { timeout: 90000 })
export const analyzeSupplier = (id) => api.post(`/suppliers/${id}/analyze`, null, { timeout: 90000 })
export const listSupplierNews = (params = {}) => api.get('/suppliers/news', { params })
export const markNewsSeen = (id) => api.post(`/suppliers/news/${id}/seen`)

// Watchlist LinkedIn
export const listWatches = (params = {}) => api.get('/watchlist', { params })
export const createWatch = (data) => api.post('/watchlist', data)
export const updateWatch = (id, data) => api.put(`/watchlist/${id}`, data)
export const deleteWatch = (id) => api.delete(`/watchlist/${id}`)
export const markWatchChecked = (id) => api.post(`/watchlist/${id}/checked`)

// Sauvegardes (admin)
export const listBackups = () => api.get('/backup')
export const runBackup = () => api.post('/backup/run', null, { timeout: 120000 })
export const restoreBackup = (filename) => api.post(`/backup/${filename}/restore`, null, { timeout: 120000 })
export const deleteBackup = (filename) => api.delete(`/backup/${filename}`)
export const downloadBackup = (filename) => api.get(`/backup/${filename}/download`, { responseType: 'blob', timeout: 120000 })

// Health
export const healthCheck = () => api.get('/health')

// Deploy (admin — option A : declenche le workflow GitHub Actions)
export const getDeployStatus = () => api.get('/admin/deploy/status')
export const triggerDeploy = (branch = 'main') => api.post('/admin/deploy', { branch })
export const getDeployRun = (runId) => api.get(`/admin/deploy/runs/${runId}`)
// Dry-run + transfert (parite Sentinel)
export const runDryRun = () => api.post('/admin/deploy/dry-run', null, { timeout: 30000 })
export const getGitStatus = () => api.get('/admin/deploy/git-status')
export const transferChanges = (message = '') => api.post('/admin/deploy/transfer', { message }, { timeout: 60000 })

export default api
